import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { compose } from 'redux';

import AJAX from 'common/AJAX';
import * as AuthRequests from 'common/auth/AuthRequests';
import { DisconnectAzureContext } from 'common/containers/AuthContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { IsWidgetContext } from 'common/containers/IsWidgetContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import LazyLoadedImage from 'common/LazyLoadedImage';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import withContexts from 'common/util/withContexts';

import AuthButton from './AuthButton';
import ForceAdminOAuth from './ForceAdminOAuth';
import Button from './inputs/Button';
import OAuthFlows from './OAuthFlows';
import Tappable from './Tappable';

import GoogleIcon from 'img/google-icon.svg';

import 'css/components/_LoginForm.scss';

class LoginForm extends Component {
  static propTypes = {
    hasSuccessAction: PropTypes.bool.isRequired,
    initiateProvider: PropTypes.string,
    isWidget: PropTypes.bool.isRequired,
    forcedMemberAuthMechanism: PropTypes.string,
    location: PropTypes.object.isRequired,
    oauthProvider: PropTypes.string,
    removeBranding: PropTypes.bool,
    onEmail: PropTypes.func.isRequired,
    onForgotPassword: PropTypes.func.isRequired,
    onSignup: PropTypes.func.isRequired,
    adminLogInURL: PropTypes.string,
    onSuccess: PropTypes.func.isRequired,
    router: PropTypes.object.isRequired,
  };

  state = {
    error: null,
  };

  componentDidMount() {
    if (this.props.initiateProvider) {
      this.onOAuth(this.props.initiateProvider);
      return;
    }

    if (!this.state.loading) {
      return;
    }

    const { location, router } = this.props;
    this.onOAuthContinue(location.query);
    router.replace(location.pathname);
  }

  onOAuth = (oauthTypeName) => {
    if (this.state.loading) {
      return;
    }

    this.setState({
      error: null,
      loading: oauthTypeName,
    });

    var OAuthFlow = OAuthFlows.getFlow(oauthTypeName);
    var flow = new OAuthFlow({
      onContinue: this.onOAuthContinue,
      onFailure: this.onOAuthFailure,
      onSuccess: this.onOAuthSuccess,
      redirectURL: null,
      separateWindow: null,
      shouldOpenNewTab: this.props.hasSuccessAction,
    });
    flow.connect();
  };

  onOAuthFailure = (error) => {
    this.setState({
      error,
    });
  };

  addAzureDomain = async (oauthData) => {
    const responseData = await AJAX.post('/api/viewer/addAzureDomain', {
      ...oauthData, // azureAccessToken, azureUserID
    });

    const { error } = parseAPIResponse(responseData, {
      isSuccessful: isDefaultSuccessResponse,
      errors: {
        'invalid email':
          'Invalid email. Please try again and make sure to sign in with your company email account.',
        default: 'Something went wrong, please try again later.',
      },
    });

    if (error) {
      if (error.type === 'invalid email') {
        const { disconnectAzure } = this.props;
        disconnectAzure();
      }

      this.setState({
        error: error.message,
      });
      return;
    }

    window.location.reload();
    return;
  };

  onOAuthSuccess = (oauthData) => {
    const { viewer } = this.props;
    if (viewer.loggedOut) {
      this.login({
        ...oauthData,
        useTokenAuth: this.props.isWidget,
      });
    } else {
      throw new Error('Unsupported OAuth method used');
    }
  };

  login = async (requestData) => {
    const { error, parsedResponse, redirecting } = await AuthRequests.login(requestData);

    if (redirecting) {
      return;
    }

    if (requestData.azureUserID) {
      await this.addAzureDomain(requestData);
      return;
    }

    if (!error) {
      this.props.onSuccess(parsedResponse);
      return;
    }

    this.setState({
      error: error.message,
      loading: null,
    });
  };

  render() {
    const loginCopy = {
      headerText: this.props.removeBranding ? 'Log in with' : 'Log in to Canny with',
      adminLinkText: this.props.removeBranding ? 'Log in with SSO' : 'Log in to Canny with SSO',
    };

    return (
      <div className="loginForm">
        <div className="topContainer">
          <h1>{loginCopy.headerText}</h1>
          <div className="buttons">
            <AuthButton
              authType="facebook"
              onFailure={this.onOAuthFailure}
              onSuccess={this.onOAuthSuccess}
              value={<div className="icon icon-facebook" />}
            />
            <AuthButton
              authType="google"
              onFailure={this.onOAuthFailure}
              onSuccess={this.onOAuthSuccess}
              value={
                <LazyLoadedImage
                  alt="google icon"
                  loading="eager"
                  src={GoogleIcon}
                  className="google"
                />
              }
            />
            <AuthButton
              authType="github"
              onFailure={this.onOAuthFailure}
              onSuccess={this.onOAuthSuccess}
              value={<div className="icon icon-github" />}
            />
            <div className="centeredDivider">OR</div>
            <Button
              buttonType="emailButton"
              onTap={this.props.onEmail}
              value={<div className="icon icon-email"></div>}
            />
          </div>
          {this.props.forcedMemberAuthMechanism ? (
            <ForceAdminOAuth
              companyID={this.props.company?._id}
              forcedMemberAuthMechanism={this.props.forcedMemberAuthMechanism}
              onFailure={this.onOAuthFailure}
              onSuccess={this.onOAuthSuccess}
              removeBranding={this.props.removeBranding}
            />
          ) : null}
          {this.state.error ? <div className="error">{this.state.error}</div> : null}
        </div>
        <div className="accountModalCTA">
          Need an account?{' '}
          <Tappable onTap={this.props.onSignup}>
            <span className="underline">Sign up</span>
          </Tappable>
        </div>
      </div>
    );
  }
}

export default compose(
  withContexts({
    company: CompanyContext,
    isWidget: IsWidgetContext,
    location: LocationContext,
    router: RouterContext,
    viewer: ViewerContext,
    disconnectAzure: DisconnectAzureContext,
  }),
)(LoginForm);
